import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from './table-management/designTokens';

interface UserAccountRow {
  id: string;
  fullName: string;
  email: string;
  role: string;
  isSynthetic: boolean;
  deactivatedAt: string | null;
  ticketNumber: string | null;
  createdAt: string;
  updatedAt: string;
}

interface PurgePreview {
  success: boolean;
  preview: boolean;
  message: string;
  impact: {
    user: { fullName: string; email: string; role: string; isSynthetic: boolean };
    registrations: number;
    checkIns: number;
    mealSelections: number;
    seatAssignments: number;
    tickets: number;
    guestRecordsUnlinked: number;
    memberUnlinked: number;
    loginAuditEntries: number;
    broadcastDismissals: number;
    stories: number;
    interviewSessions: number;
  };
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    marginBottom: '1rem',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    cursor: 'pointer',
    userSelect: 'none',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
  },
  badge: {
    fontSize: '0.75rem',
    fontWeight: 500,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
    backgroundColor: BG_SURFACE,
    color: TEXT_MUTED,
  },
  chevron: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    transition: 'transform 0.2s',
  },
  body: {
    padding: '0 1.25rem 1.25rem',
  },
  searchInput: {
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '0.85rem',
    width: '100%',
    maxWidth: '350px',
    marginBottom: '1rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.5rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    fontWeight: 600,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.55rem 0.5rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    verticalAlign: 'middle' as const,
    color: TEXT_DARK,
  },
  syntheticBadge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 500,
    padding: '0.15rem 0.45rem',
    borderRadius: '4px',
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
  },
  memberBadge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 500,
    padding: '0.15rem 0.45rem',
    borderRadius: '4px',
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  statusActive: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#2e7d32',
  },
  statusDeactivated: {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#d32f2f',
  },
  credential: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    color: TEXT_DARK,
  },
  credentialNote: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    fontStyle: 'italic',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginBottom: '0.75rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function UserAccountsPanel() {
  const [accounts, setAccounts] = useState<UserAccountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  // Purge state
  const [purgePreview, setPurgePreview] = useState<PurgePreview | null>(null);
  const [purgeTargetId, setPurgeTargetId] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchAccounts() {
      try {
        const data = await apiClient<UserAccountRow[]>('/guest-accounts/accounts');
        if (!cancelled) {
          setAccounts(data);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as { message?: string };
          setError(apiErr.message || 'Failed to load user accounts.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAccounts();
    return () => { cancelled = true; };
  }, []);

  const filtered = accounts.filter((a) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.fullName.toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q)
    );
  });

  const handlePurgePreview = async (userId: string) => {
    setPurgeTargetId(userId);
    setPurgeResult(null);
    try {
      const preview = await apiClient<PurgePreview>(`/users/${userId}/purge?preview=true`, { method: 'DELETE' });
      setPurgePreview(preview);
    } catch {
      setPurgePreview(null);
      alert('Failed to load purge preview.');
    }
  };

  const handlePurgeConfirm = async () => {
    if (!purgeTargetId) return;
    setPurging(true);
    try {
      const result = await apiClient<{ success: boolean; message: string }>(`/users/${purgeTargetId}/purge`, { method: 'DELETE' });
      setPurgeResult(result.message);
      setAccounts((prev) => prev.filter((a) => a.id !== purgeTargetId));
      setTimeout(() => {
        setPurgePreview(null);
        setPurgeTargetId(null);
        setPurgeResult(null);
      }, 2500);
    } catch {
      setPurgeResult('Failed to purge user.');
    } finally {
      setPurging(false);
    }
  };

  const handlePurgeCancel = () => {
    setPurgePreview(null);
    setPurgeTargetId(null);
    setPurgeResult(null);
  };

  return (
    <div style={styles.container}>
      <div
        style={styles.header}
        onClick={() => setCollapsed(!collapsed)}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-label="Toggle User Accounts panel"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
      >
        <div style={styles.headerLeft}>
          <h3 style={styles.title}>User Accounts</h3>
          {!loading && (
            <span style={styles.badge}>{accounts.length}</span>
          )}
        </div>
        <span style={{ ...styles.chevron, transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)' }}>
          ▶
        </span>
      </div>

      {!collapsed && (
        <div style={styles.body}>
          {error && <div style={styles.error}>{error}</div>}

          {loading ? (
            <div style={styles.loading}>Loading user accounts...</div>
          ) : (
            <>
              <input
                type="text"
                placeholder="Search by name or email..."
                style={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search user accounts"
              />

              {filtered.length === 0 ? (
                <div style={styles.emptyState}>
                  {accounts.length === 0
                    ? 'No user accounts found.'
                    : 'No accounts match your search.'}
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Full Name</th>
                        <th style={styles.th}>Login Email</th>
                        <th style={styles.th}>Password/Credential</th>
                        <th style={styles.th}>Access Level</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Created</th>
                        <th style={styles.th}>Last Updated</th>
                        <th style={styles.th}>Status</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((account) => (
                        <tr key={account.id}>
                          <td style={styles.td}>{account.fullName}</td>
                          <td style={styles.td}>{account.email}</td>
                          <td style={styles.td}>
                            {account.isSynthetic && account.ticketNumber ? (
                              <span style={styles.credential}>{account.ticketNumber}</span>
                            ) : (
                              <span style={styles.credentialNote}>Uses existing password</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <select
                              value={account.role}
                              onChange={async (e) => {
                                const newRole = e.target.value;
                                try {
                                  await apiClient(`/users/${account.id}/role`, { method: 'PATCH', body: { role: newRole } });
                                  setAccounts((prev) => prev.map((a) => a.id === account.id ? { ...a, role: newRole } : a));
                                } catch { alert('Failed to change role.'); }
                              }}
                              style={{ fontSize: '0.8rem', padding: '3px 6px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#fff' }}
                              aria-label={`Change role for ${account.fullName}`}
                            >
                              <option value="Admin">Admin</option>
                              <option value="Organizer">Organizer</option>
                              <option value="Delegate">Delegate</option>
                              <option value="Attendee">Attendee</option>
                            </select>
                          </td>
                          <td style={styles.td}>
                            {account.isSynthetic ? (
                              <span style={styles.syntheticBadge}>Synthetic</span>
                            ) : (
                              <span style={styles.memberBadge}>Member</span>
                            )}
                          </td>
                          <td style={styles.td}>{formatDate(account.createdAt)}</td>
                          <td style={styles.td}>{formatDate(account.updatedAt)}</td>
                          <td style={styles.td}>
                            {account.deactivatedAt ? (
                              <span style={styles.statusDeactivated}>Deactivated</span>
                            ) : (
                              <span style={styles.statusActive}>Active</span>
                            )}
                          </td>
                          <td style={styles.td}>
                            <button
                              onClick={() => handlePurgePreview(account.id)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', fontSize: '0.9rem', borderRadius: '4px', lineHeight: 1 }}
                              title="Purge User"
                              aria-label={`Purge ${account.fullName}`}
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Purge Confirmation Modal */}
      {purgePreview && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: '1rem' }}
          onClick={handlePurgeCancel}
          role="dialog"
          aria-modal="true"
          aria-label="Purge user confirmation"
        >
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', maxWidth: '480px', width: '100%', padding: '1.5rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            {purgeResult ? (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
                <p style={{ color: TEXT_DARK, fontWeight: 600 }}>{purgeResult}</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#d32f2f' }}>⚠️ Purge User</h3>
                  <button onClick={handlePurgeCancel} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#666' }} aria-label="Close">✕</button>
                </div>

                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#991b1b', fontWeight: 600 }}>
                    This will permanently delete "{purgePreview.impact.user.fullName}" ({purgePreview.impact.user.email})
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: '#991b1b' }}>
                    This action cannot be undone. An audit record will be saved.
                  </p>
                </div>

                <div style={{ fontSize: '0.85rem', color: TEXT_DARK, marginBottom: '1rem' }}>
                  <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Records affected:</p>
                  <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                    <tbody>
                      {purgePreview.impact.registrations > 0 && <tr><td style={{ padding: '3px 0' }}>Registrations</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.registrations}</td></tr>}
                      {purgePreview.impact.checkIns > 0 && <tr><td style={{ padding: '3px 0' }}>Check-ins</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.checkIns}</td></tr>}
                      {purgePreview.impact.mealSelections > 0 && <tr><td style={{ padding: '3px 0' }}>Meal selections</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.mealSelections}</td></tr>}
                      {purgePreview.impact.seatAssignments > 0 && <tr><td style={{ padding: '3px 0' }}>Seat assignments</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.seatAssignments}</td></tr>}
                      {purgePreview.impact.tickets > 0 && <tr><td style={{ padding: '3px 0' }}>Tickets deleted</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.tickets}</td></tr>}
                      {purgePreview.impact.guestRecordsUnlinked > 0 && <tr><td style={{ padding: '3px 0' }}>Guest records (unlinked, preserved)</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.guestRecordsUnlinked}</td></tr>}
                      {purgePreview.impact.memberUnlinked > 0 && <tr><td style={{ padding: '3px 0' }}>Member record (unlinked, preserved)</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.memberUnlinked}</td></tr>}
                      {purgePreview.impact.loginAuditEntries > 0 && <tr><td style={{ padding: '3px 0' }}>Login audit entries</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.loginAuditEntries}</td></tr>}
                      {purgePreview.impact.stories > 0 && <tr><td style={{ padding: '3px 0' }}>Stories</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.stories}</td></tr>}
                      {purgePreview.impact.interviewSessions > 0 && <tr><td style={{ padding: '3px 0' }}>Interview sessions</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{purgePreview.impact.interviewSessions}</td></tr>}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={handlePurgeCancel} style={{ padding: '0.5rem 1.25rem', backgroundColor: '#e0e0e0', color: '#333', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
                  <button onClick={handlePurgeConfirm} disabled={purging} style={{ padding: '0.5rem 1.25rem', backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem', opacity: purging ? 0.6 : 1 }}>
                    {purging ? 'Purging…' : 'Confirm Purge'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
